import { getApiUrl } from "@lib/utils";
import { useMutation } from "@tanstack/react-query";

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface LoginResponse {
  token: string;
}

async function loginApi(credentials: LoginCredentials): Promise<LoginResponse> {
  const apiUrl = getApiUrl();
  const response = await fetch(`${apiUrl}/auth/login`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(credentials),
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ detail: "Login failed" }));
    // Handle nested error formats:
    // - {detail: {error: "..."}} - FastAPI nested format
    // - {detail: "..."} - FastAPI simple format
    // - {error: "..."} - Simple format
    let errorMessage = "Invalid credentials";
    if (error.detail) {
      if (typeof error.detail === "string") {
        errorMessage = error.detail;
      } else if (error.detail.error) {
        errorMessage = error.detail.error;
      }
    } else if (error.error) {
      errorMessage =
        typeof error.error === "string"
          ? error.error
          : error.error.message || "Invalid credentials";
    }
    throw new Error(errorMessage);
  }

  return response.json();
}

export function useLogin() {
  return useMutation({
    mutationFn: loginApi,
  });
}

async function checkIsAdminApi(): Promise<{ is_admin: boolean }> {
  const token = getAuthToken();
  if (!token) {
    throw new Error("No authentication token found");
  }

  const apiUrl = getApiUrl();
  const response = await fetch(`${apiUrl}/user/is-admin?token=${token}`, {
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ detail: "Request failed" }));
    throw new Error(error.detail || error.message || "Request failed");
  }

  return response.json();
}

function getAuthToken(): string | null {
  return localStorage.getItem("admin_token");
}

export function useCheckIsAdmin() {
  return useMutation({
    mutationFn: checkIsAdminApi,
  });
}
